import { evalConditionalRule } from './sheetConditionalLogic';
import { resolveHeaderFormulaReferences, resolveHeaderReferences } from './sheetHeaderReferences';

const FORMULA_FUNCTIONS = new Set(['SUM', 'AVG', 'MIN', 'MAX', 'CONCAT', 'DATEDIFF_DAYS', 'DATETIMEDIFF_DH', 'TIMEDIFF_HOURS', 'DAY', 'WEEK', 'MONTH', 'LOOKUP', 'FILTERLIST', 'SUBTRACTLIST']);
const NUMERIC_HEADER_TYPES = new Set(['number', 'rating', 'currency', 'percent']);
const FILTER_LIST_OPERATORS = new Set(['equals', 'notEquals', 'greaterThan', 'lessThan', 'greaterThanOrEqual', 'lessThanOrEqual', 'includes', 'in']);
export const FILTER_LIST_OPERATOR_OPTIONS = [
  { value: 'equals', label: 'Equals' },
  { value: 'notEquals', label: 'Not equals' },
  { value: 'greaterThan', label: 'Greater than' },
  { value: 'lessThan', label: 'Less than' },
  { value: 'greaterThanOrEqual', label: 'Greater than or equal' },
  { value: 'lessThanOrEqual', label: 'Less than or equal' },
  { value: 'includes', label: 'Includes' },
  { value: 'in', label: 'In' }
];
const VISUAL_FORMULA_DEFINITIONS = {
  sum: { label: 'Sum', mode: 'function', functionName: 'SUM', numericOnly: true },
  subtract: { label: 'Subtract', mode: 'operator', operator: '-', numericOnly: true },
  multiply: { label: 'Multiply', mode: 'operator', operator: '*', numericOnly: true },
  divide: { label: 'Divide', mode: 'operator', operator: '/', numericOnly: true },
  percentage: { label: 'Percentage', mode: 'percentage', numericOnly: true },
  average: { label: 'Average', mode: 'function', functionName: 'AVG', numericOnly: true },
  min: { label: 'Lowest value', mode: 'function', functionName: 'MIN', numericOnly: true },
  max: { label: 'Highest value', mode: 'function', functionName: 'MAX', numericOnly: true },
  concat: { label: 'Join text', mode: 'function', functionName: 'CONCAT', numericOnly: false },
  duration_days: { label: 'Date duration (days)', mode: 'duration', functionName: 'DATEDIFF_DAYS', numericOnly: false },
  duration_day_hours: { label: 'DateTime duration (days + hours)', mode: 'duration', functionName: 'DATETIMEDIFF_DH', numericOnly: false },
  duration_hours: { label: 'Time duration (hours)', mode: 'duration', functionName: 'TIMEDIFF_HOURS', numericOnly: false },
  day: { label: 'Day from date', mode: 'date_part', functionName: 'DAY', numericOnly: false },
  week: { label: 'Week from date', mode: 'date_part', functionName: 'WEEK', numericOnly: false },
  month: { label: 'Month from date', mode: 'date_part', functionName: 'MONTH', numericOnly: false },
  lookup: { label: 'Lookup from table', mode: 'lookup', functionName: 'LOOKUP', numericOnly: false },
  filter_list: { label: 'Filter list from table', mode: 'filter_list', functionName: 'FILTERLIST', numericOnly: false },
  subtract_list: { label: 'Subtract one list from another', mode: 'function', functionName: 'SUBTRACTLIST', numericOnly: false }
};
const VISUAL_FORMULA_OPERATORS = new Set(['+', '-', '*', '/']);
const BASE_REFERENCE_PREFIX = 'base:';
const DURATION_WEEKEND_MODE_VALUES = new Set(['include', 'exclude']);

export const VISUAL_FORMULA_OPTIONS = Object.entries(VISUAL_FORMULA_DEFINITIONS).map(([value, config]) => ({
  value,
  label: config.label
}));
export const getFilterListOperatorLabel = (value) => (
  FILTER_LIST_OPERATOR_OPTIONS.find((option) => option.value === value)?.label || 'Equals'
);

export const getColumnLetter = (index) => {
  let value = index + 1;
  let result = '';

  while (value > 0) {
    const remainder = (value - 1) % 26;
    result = String.fromCharCode(65 + remainder) + result;
    value = Math.floor((value - 1) / 26);
  }

  return result;
};

export const hasHeaderFormula = (header) => Boolean(`${header?.formula || ''}`.trim());
export const hasHeaderBaseValue = (header) => header?.baseValue !== '' && header?.baseValue !== null && header?.baseValue !== undefined;
export const hasConditionalFormulaRules = (header) => Array.isArray(header?.conditionalFormulaRules) && header.conditionalFormulaRules.some((rule) => `${rule?.formula || ''}`.trim());

const isBaseOperandReference = (operand) => `${operand || ''}`.startsWith(BASE_REFERENCE_PREFIX);
const getOperandHeaderId = (operand) => isBaseOperandReference(operand) ? `${operand}`.slice(BASE_REFERENCE_PREFIX.length) : operand;
const toBaseOperandReference = (headerId) => `${BASE_REFERENCE_PREFIX}${headerId}`;
const buildReferenceExpression = (operand, headers) => getOperandLetter(operand, headers);
const getResolvedOperandId = (operand) => isBaseOperandReference(operand) ? `${operand}`.slice(BASE_REFERENCE_PREFIX.length) : operand;
const normalizePercentageConfig = (config = {}) => {
  const denominatorId = getResolvedOperandId(config?.denominator || '');
  return {
    ...config,
    numeratorOperands: (Array.isArray(config?.numeratorOperands) ? config.numeratorOperands : []).filter((operand) => (
      denominatorId ? getResolvedOperandId(operand) !== denominatorId : true
    ))
  };
};

const normalizeLookupConfig = (config = {}) => ({
  type: 'lookup',
  lookupSheetId: `${config?.lookupSheetId || ''}`.trim(),
  lookupMatchSourceHeaderId: `${config?.lookupMatchSourceHeaderId || ''}`.trim(),
  lookupMatchCurrentHeaderId: `${config?.lookupMatchCurrentHeaderId || ''}`.trim(),
  lookupReturnHeaderId: `${config?.lookupReturnHeaderId || ''}`.trim()
});

const normalizeFilterListConfig = (config = {}) => ({
  type: 'filter_list',
  filterListSheetId: `${config?.filterListSheetId || ''}`.trim(),
  filterListReturnHeaderId: `${config?.filterListReturnHeaderId || ''}`.trim(),
  filterListFilters: (Array.isArray(config?.filterListFilters) ? config.filterListFilters : [])
    .map((filter) => ({
      sourceHeaderId: `${filter?.sourceHeaderId || ''}`.trim(),
      operator: FILTER_LIST_OPERATORS.has(`${filter?.operator || ''}`.trim()) ? `${filter?.operator || ''}`.trim() : 'equals',
      currentHeaderId: `${filter?.currentHeaderId || ''}`.trim()
    }))
    .filter((filter) => filter.sourceHeaderId && filter.currentHeaderId)
});

const normalizeDurationWeekendMode = (value) => (
  DURATION_WEEKEND_MODE_VALUES.has(`${value || ''}`.trim()) ? `${value || ''}`.trim() : 'include'
);

const DATE_PART_FORMULA_TYPES = new Set(['day', 'week', 'month']);

const buildFormulaExpressionFromConfig = (config = {}, headers = []) => {
  const definition = VISUAL_FORMULA_DEFINITIONS[config?.type];
  if (!definition) return '';

  if (definition.mode === 'percentage') {
    const numeratorType = config?.numeratorType || 'single';
    const numeratorOperands = Array.isArray(config?.numeratorOperands) ? config.numeratorOperands : [];
    const denominatorExpression = buildReferenceExpression(config?.denominator, headers);

    if (!denominatorExpression) return '';

    const numeratorExpression = numeratorType === 'single'
      ? buildReferenceExpression(numeratorOperands[0], headers)
      : buildFormulaExpressionFromConfig({ type: numeratorType, operands: numeratorOperands }, headers);

    if (!numeratorExpression) return '';
    return `(${numeratorExpression} / ${denominatorExpression}) * 100`;
  }

  if (definition.mode === 'lookup') {
    const lookupConfig = normalizeLookupConfig(config);
    if (
      !lookupConfig.lookupSheetId ||
      !lookupConfig.lookupMatchSourceHeaderId ||
      !lookupConfig.lookupMatchCurrentHeaderId ||
      !lookupConfig.lookupReturnHeaderId
    ) {
      return '';
    }

    return `LOOKUP("${lookupConfig.lookupSheetId.replace(/"/g, '\\"')}","${lookupConfig.lookupMatchSourceHeaderId.replace(/"/g, '\\"')}","${lookupConfig.lookupMatchCurrentHeaderId.replace(/"/g, '\\"')}","${lookupConfig.lookupReturnHeaderId.replace(/"/g, '\\"')}")`;
  }

  if (definition.mode === 'filter_list') {
    const filterListConfig = normalizeFilterListConfig(config);
    if (!filterListConfig.filterListSheetId || !filterListConfig.filterListReturnHeaderId) {
      return '';
    }
    const filterArgs = filterListConfig.filterListFilters.flatMap((filter) => {
      const currentReference = buildReferenceExpression(filter.currentHeaderId, headers);
      if (!filter.sourceHeaderId || !currentReference) return [];
      return [
        `"${filter.sourceHeaderId.replace(/"/g, '\\"')}"`,
        `"${(filter.operator || 'equals').replace(/"/g, '\\"')}"`,
        currentReference
      ];
    });
    const allArgs = [
      `"${filterListConfig.filterListSheetId.replace(/"/g, '\\"')}"`,
      `"${filterListConfig.filterListReturnHeaderId.replace(/"/g, '\\"')}"`,
      ...filterArgs
    ];
    return `FILTERLIST(${allArgs.join(', ')})`;
  }

  const references = (Array.isArray(config?.operands) ? config.operands : [])
    .filter((operand) => headers.some((header) => header.id === getOperandHeaderId(operand)))
    .map((operand) => buildReferenceExpression(operand, headers))
    .filter(Boolean);

  if (!references.length) return '';

  if (definition.mode === 'operator') {
    if (references.length < 2) return '';
    return `${references.join(` ${definition.operator} `)}`;
  }

  if (definition.mode === 'duration') {
    const orderedReferences = references.slice(0, 2);
    if (orderedReferences.length < 2) return '';
    if (config?.type === 'duration_days') {
      const weekendMode = normalizeDurationWeekendMode(config?.weekendMode);
      return weekendMode === 'exclude'
        ? `${definition.functionName}(${orderedReferences.join(', ')}, "exclude_weekends")`
        : `${definition.functionName}(${orderedReferences.join(', ')})`;
    }
    return `${definition.functionName}(${orderedReferences.join(', ')})`;
  }

  if (definition.mode === 'date_part') {
    return references.length ? `${definition.functionName}(${references[0]})` : '';
  }

  if (definition.functionName === 'CONCAT') {
    const separator = `${config?.separator || ''}`;
    const args = references.flatMap((reference, index) => {
      if (!separator || index === 0) return [reference];
      return [`"${separator.replace(/"/g, '\\"')}"`, reference];
    });
    return `${definition.functionName}(${args.join(', ')})`;
  }

  return `${definition.functionName}(${references.join(', ')})`;
};

const getOperandLetter = (operand, headers) => {
  const headerId = getOperandHeaderId(operand);
  const headerIndex = headers.findIndex((header) => header.id === headerId);
  if (headerIndex < 0) return '';
  const letter = getColumnLetter(headerIndex);
  return isBaseOperandReference(operand) ? `BASE(${letter})` : letter;
};

const stripOuterParentheses = (input = '') => {
  let value = `${input || ''}`.trim();

  const hasWrappingParentheses = (expression) => {
    if (!expression.startsWith('(') || !expression.endsWith(')')) return false;
    let depth = 0;
    for (let index = 0; index < expression.length; index += 1) {
      const character = expression[index];
      if (character === '(') depth += 1;
      if (character === ')') depth -= 1;
      if (depth === 0 && index < expression.length - 1) return false;
    }
    return depth === 0;
  };

  while (hasWrappingParentheses(value)) {
    value = value.slice(1, -1).trim();
  }

  return value;
};

const findTopLevelOperatorIndex = (input = '', operator) => {
  let depth = 0;
  let quote = null;

  for (let index = 0; index < input.length; index += 1) {
    const character = input[index];

    if ((character === '"' || character === '\'') && input[index - 1] !== '\\') {
      quote = quote === character ? null : (!quote ? character : quote);
      continue;
    }

    if (quote) continue;
    if (character === '(') depth += 1;
    if (character === ')') depth -= 1;
    if (depth === 0 && character === operator) return index;
  }

  return -1;
};

export const buildFormulaFromConfig = (config = {}, headers = []) => {
  const expression = buildFormulaExpressionFromConfig(config, headers);
  return expression ? `=${expression}` : '';
};

export const sanitizeHeaderConditionalFormulaRules = (rules = [], headers = [], currentHeaderId = '') => {
  const validHeaderIds = new Set(
    (Array.isArray(headers) ? headers : [])
      .map((header) => `${header?.id || ''}`.trim())
      .filter((headerId) => headerId && headerId !== currentHeaderId)
  );

  return (Array.isArray(rules) ? rules : [])
    .map((rule) => {
      const headerId = `${rule?.headerId || rule?.fieldId || ''}`.trim();
      const formula = `${rule?.formula || ''}`.trim();
      if (!headerId || !validHeaderIds.has(headerId) || !formula) return null;
      return {
        headerId,
        operator: `${rule?.operator || 'equals'}`.trim() || 'equals',
        value: `${rule?.value ?? ''}`,
        formula
      };
    })
    .filter(Boolean);
};

export const parseFormulaToConfig = (formula = '', headers = [], currentHeaderId = '') => {
  const normalized = resolveHeaderFormulaReferences(
    `${formula || ''}`.trim().replace(/^=/, ''),
    headers,
    currentHeaderId,
    (header) => {
      const index = headers.findIndex((candidate) => candidate.id === header.id);
      return index >= 0 ? getColumnLetter(index) : '';
    }
  );
  if (!normalized) return null;

  const resolveHeaderIdByLetter = (letter) => {
    const index = headers.findIndex((_, headerIndex) => getColumnLetter(headerIndex) === letter);
    return index >= 0 ? headers[index].id : null;
  };
  const resolveOperandReference = (value) => {
    const trimmed = `${value || ''}`.trim();
    const baseMatch = trimmed.match(/^BASE\(([A-Z]+)\)$/i);
    if (baseMatch) {
      const headerId = resolveHeaderIdByLetter(baseMatch[1].toUpperCase());
      return headerId ? toBaseOperandReference(headerId) : null;
    }
    return resolveHeaderIdByLetter(trimmed.toUpperCase());
  };
  const parseNumericExpressionToConfig = (expression) => {
    const raw = stripOuterParentheses(`${expression || ''}`.trim());
    if (!raw) return null;

    const functionMatch = raw.match(/^([A-Z_]+)\((.*)\)$/i);
    if (functionMatch) {
      const functionName = functionMatch[1].toUpperCase();
      const args = splitFormulaArgs(functionMatch[2]);
      const typeMap = {
        SUM: 'sum',
        AVG: 'average',
        MIN: 'min',
        MAX: 'max'
      };

      if (!typeMap[functionName]) return null;

      const operands = args
        .map((arg) => resolveOperandReference(arg))
        .filter(Boolean);

      return operands.length ? { type: typeMap[functionName], operands } : null;
    }

    const compactExpression = raw.replace(/\s+/g, '');
    const parts = compactExpression.split(/([+\-*/])/).filter(Boolean);
    const operators = parts.filter((_, index) => index % 2 === 1);
    const references = parts.filter((_, index) => index % 2 === 0);

    if (
      parts.length >= 3 &&
      operators.length &&
      operators.every((operator) => operator === operators[0] && VISUAL_FORMULA_OPERATORS.has(operator)) &&
      references.every((reference) => /^[A-Z]+$/i.test(reference) || /^BASE\([A-Z]+\)$/i.test(reference))
    ) {
      const operands = references
        .map((reference) => resolveOperandReference(reference))
        .filter(Boolean);

      if (operands.length === references.length) {
        const typeMap = {
          '+': 'sum',
          '-': 'subtract',
          '*': 'multiply',
          '/': 'divide'
        };
        return {
          type: typeMap[operators[0]],
          operands
        };
      }
    }

    const singleOperand = resolveOperandReference(raw);
    return singleOperand ? { type: 'single', operands: [singleOperand] } : null;
  };

  const functionMatch = normalized.match(/^([A-Z_]+)\((.*)\)$/i);
  if (functionMatch) {
    const functionName = functionMatch[1].toUpperCase();
    const args = splitFormulaArgs(functionMatch[2]);

    if (functionName === 'SUM' || functionName === 'AVG' || functionName === 'MIN' || functionName === 'MAX') {
      const typeMap = {
        SUM: 'sum',
        AVG: 'average',
        MIN: 'min',
        MAX: 'max'
      };
      const operands = args
        .map((arg) => resolveOperandReference(arg))
        .filter(Boolean);

      return operands.length ? { type: typeMap[functionName], operands } : null;
    }

    if (functionName === 'DATEDIFF_DAYS' || functionName === 'DATETIMEDIFF_DH' || functionName === 'TIMEDIFF_HOURS') {
      const operands = args
        .slice(0, 2)
        .map((arg) => resolveOperandReference(arg))
        .filter(Boolean);
      const typeMap = {
        DATEDIFF_DAYS: 'duration_days',
        DATETIMEDIFF_DH: 'duration_day_hours',
        TIMEDIFF_HOURS: 'duration_hours'
      };
      if (operands.length < 2) return null;
      if (functionName === 'DATEDIFF_DAYS') {
        const modeArg = `${args[2] || ''}`.trim().replace(/^["']|["']$/g, '').toLowerCase();
        return {
          type: typeMap[functionName],
          operands,
          weekendMode: modeArg === 'exclude_weekends' ? 'exclude' : 'include'
        };
      }
      return { type: typeMap[functionName], operands };
    }

    if (functionName === 'DAY' || functionName === 'WEEK' || functionName === 'MONTH') {
      const operands = args
        .slice(0, 1)
        .map((arg) => resolveOperandReference(arg))
        .filter(Boolean);
      const typeMap = {
        DAY: 'day',
        WEEK: 'week',
        MONTH: 'month'
      };
      return operands.length ? { type: typeMap[functionName], operands: operands.slice(0, 1) } : null;
    }

    if (functionName === 'LOOKUP') {
      const normalizedArgs = args.map((arg) => `${arg || ''}`.trim());
      const unquote = (value = '') => {
        if (
          (value.startsWith('"') && value.endsWith('"')) ||
          (value.startsWith('\'') && value.endsWith('\''))
        ) {
          return value.slice(1, -1);
        }
        return value;
      };

      const lookupConfig = normalizeLookupConfig({
        lookupSheetId: unquote(normalizedArgs[0]),
        lookupMatchSourceHeaderId: unquote(normalizedArgs[1]),
        lookupMatchCurrentHeaderId: unquote(normalizedArgs[2]),
        lookupReturnHeaderId: unquote(normalizedArgs[3])
      });

      return (
        lookupConfig.lookupSheetId &&
        lookupConfig.lookupMatchSourceHeaderId &&
        lookupConfig.lookupMatchCurrentHeaderId &&
        lookupConfig.lookupReturnHeaderId
      )
        ? lookupConfig
        : null;
    }

    if (functionName === 'FILTERLIST') {
      const normalizedArgs = args.map((arg) => `${arg || ''}`.trim());
      const unquote = (value = '') => {
        if (
          (value.startsWith('"') && value.endsWith('"')) ||
          (value.startsWith('\'') && value.endsWith('\''))
        ) {
          return value.slice(1, -1);
        }
        return value;
      };

      const filterListSheetId = unquote(normalizedArgs[0]);
      const filterListReturnHeaderId = unquote(normalizedArgs[1]);
      const filterListFilters = [];

      for (let index = 2; index < normalizedArgs.length;) {
        const sourceHeaderId = unquote(normalizedArgs[index]);
        if (!sourceHeaderId) {
          index += 1;
          continue;
        }
        const maybeOperator = unquote(normalizedArgs[index + 1]);
        const hasExplicitOperator = FILTER_LIST_OPERATORS.has(maybeOperator);
        const operator = hasExplicitOperator ? maybeOperator : 'equals';
        const currentHeaderId = resolveOperandReference(normalizedArgs[index + (hasExplicitOperator ? 2 : 1)]);
        if (!sourceHeaderId || !currentHeaderId) {
          index += hasExplicitOperator ? 3 : 2;
          continue;
        }
        filterListFilters.push({ sourceHeaderId, operator, currentHeaderId });
        index += hasExplicitOperator ? 3 : 2;
      }

      const filterListConfig = normalizeFilterListConfig({
        filterListSheetId,
        filterListReturnHeaderId,
        filterListFilters
      });

      return (
        filterListConfig.filterListSheetId &&
        filterListConfig.filterListReturnHeaderId
      ) ? filterListConfig : null;
    }

    if (functionName === 'SUBTRACTLIST') {
      const operands = args
        .map((arg) => resolveOperandReference(arg))
        .filter(Boolean);

      return operands.length >= 2 ? { type: 'subtract_list', operands: operands.slice(0, 2) } : null;
    }

    if (functionName === 'CONCAT') {
      const operands = [];
      const separators = [];

      args.forEach((arg) => {
        const trimmed = arg.trim();
        const operandRef = resolveOperandReference(trimmed);
        if (operandRef && !isBaseOperandReference(operandRef)) {
          operands.push(operandRef);
          return;
        }

        if (
          (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
          (trimmed.startsWith('\'') && trimmed.endsWith('\''))
        ) {
          separators.push(trimmed.slice(1, -1));
        }
      });

      if (!operands.length) return null;
      const uniqueSeparators = Array.from(new Set(separators));
      return {
        type: 'concat',
        operands,
        separator: uniqueSeparators.length === 1 ? uniqueSeparators[0] : ''
      };
    }
  }

  const compact = normalized.replace(/\s+/g, '');
  const parts = compact.split(/([+\-*/])/).filter(Boolean);
  const operators = parts.filter((_, index) => index % 2 === 1);
  const references = parts.filter((_, index) => index % 2 === 0);

  if (
    parts.length >= 3 &&
    operators.length &&
    operators.every((operator) => operator === operators[0] && VISUAL_FORMULA_OPERATORS.has(operator)) &&
    references.every((reference) => /^[A-Z]+$/i.test(reference) || /^BASE\([A-Z]+\)$/i.test(reference))
  ) {
    const operands = references
      .map((reference) => resolveOperandReference(reference))
      .filter(Boolean);

    if (operands.length === references.length) {
      const typeMap = {
        '+': 'sum',
        '-': 'subtract',
        '*': 'multiply',
        '/': 'divide'
      };
      return {
        type: typeMap[operators[0]],
        operands
      };
    }
  }

  const compactNormalized = normalized.replace(/\s+/g, '');
  if (compactNormalized.endsWith('*100')) {
    const percentageCore = stripOuterParentheses(compactNormalized.slice(0, -4));
    const divisionIndex = findTopLevelOperatorIndex(percentageCore, '/');
    const numeratorExpression = divisionIndex >= 0 ? percentageCore.slice(0, divisionIndex) : '';
    const denominatorExpression = divisionIndex >= 0 ? percentageCore.slice(divisionIndex + 1) : '';
    const numeratorConfig = parseNumericExpressionToConfig(numeratorExpression);
    const denominator = resolveOperandReference(denominatorExpression);

    if (numeratorConfig && denominator) {
      return normalizePercentageConfig({
        type: 'percentage',
        numeratorType: numeratorConfig.type,
        numeratorOperands: numeratorConfig.operands,
        denominator
      });
    }
  }

  return null;
};

const splitFormulaArgs = (input = '') => {
  const parts = [];
  let current = '';
  let depth = 0;
  let quote = null;

  for (let index = 0; index < input.length; index += 1) {
    const character = input[index];

    if ((character === '"' || character === '\'') && input[index - 1] !== '\\') {
      if (quote === character) {
        quote = null;
      } else if (!quote) {
        quote = character;
      }
      current += character;
      continue;
    }

    if (!quote) {
      if (character === '(') depth += 1;
      if (character === ')') depth -= 1;
      if (character === ',' && depth === 0) {
        parts.push(current.trim());
        current = '';
        continue;
      }
    }

    current += character;
  }

  if (current.trim()) {
    parts.push(current.trim());
  }

  return parts;
};

const parseDateValue = (value) => {
  const trimmed = `${value || ''}`.trim();
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, year, month, day] = match;
  const timestamp = Date.UTC(Number(year), Number(month) - 1, Number(day));
  return Number.isFinite(timestamp) ? timestamp : null;
};

const parseDateTimeValue = (value) => {
  const trimmed = `${value || ''}`.trim();
  const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const [, year, month, day, hours, minutes, seconds] = match;
  const timestamp = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours),
    Number(minutes),
    Number(seconds || 0)
  ).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

const parseTimeValueToMinutes = (value) => {
  const trimmed = `${value || ''}`.trim();
  const match = trimmed.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const [, hours, minutes, seconds] = match;
  const totalMinutes = (Number(hours) * 60) + Number(minutes) + (Number(seconds || 0) / 60);
  return Number.isFinite(totalMinutes) ? totalMinutes : null;
};

const parseDateObjectValue = (value) => {
  const directDate = parseDateValue(value);
  if (directDate !== null) return new Date(directDate);

  const directDateTime = parseDateTimeValue(value);
  if (directDateTime !== null) return new Date(directDateTime);

  return null;
};

const getIsoWeekNumber = (date) => {
  const workingDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNumber = workingDate.getUTCDay() || 7;
  workingDate.setUTCDate(workingDate.getUTCDate() + 4 - dayNumber);
  const yearStart = new Date(Date.UTC(workingDate.getUTCFullYear(), 0, 1));
  return Math.ceil((((workingDate - yearStart) / 86400000) + 1) / 7);
};

const formatDayHourDuration = (totalMilliseconds) => {
  if (!Number.isFinite(totalMilliseconds)) return '';
  const sign = totalMilliseconds < 0 ? '-' : '';
  const absoluteMilliseconds = Math.abs(totalMilliseconds);
  const totalHours = Math.floor(absoluteMilliseconds / (60 * 60 * 1000));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  return `${sign}${days}d ${hours}h`;
};

const countWeekdaysBetweenDates = (startTimestamp, endTimestamp) => {
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const totalDayDelta = Math.round((endTimestamp - startTimestamp) / millisecondsPerDay);
  if (!Number.isFinite(totalDayDelta) || totalDayDelta === 0) return 0;

  const direction = totalDayDelta > 0 ? 1 : -1;
  const absoluteDays = Math.abs(totalDayDelta);
  const normalizedStart = direction > 0 ? startTimestamp : endTimestamp;
  const startDayOfWeek = new Date(normalizedStart).getUTCDay();
  const fullWeeks = Math.floor(absoluteDays / 7);
  const remainingDays = absoluteDays % 7;
  let weekdays = fullWeeks * 5;

  for (let offset = 1; offset <= remainingDays; offset += 1) {
    const dayOfWeek = (startDayOfWeek + offset) % 7;
    if (dayOfWeek >= 1 && dayOfWeek <= 5) {
      weekdays += 1;
    }
  }

  return weekdays * direction;
};

const normalizeListValue = (value) => {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => normalizeListValue(item))
      .map((item) => `${item ?? ''}`.trim())
      .filter(Boolean);
  }
  if (value === null || value === undefined) return [];
  return [`${value}`.trim()].filter(Boolean);
};

const uniqueListValue = (value) => Array.from(new Set(normalizeListValue(value)));

const coerceResolvedValue = (header, value) => {
  if (Array.isArray(value)) return uniqueListValue(value);
  if (header?.type === 'checkbox') return Boolean(value);
  if (header?.type === 'multiselect' || header?.type === 'list') return uniqueListValue(value);
  if (NUMERIC_HEADER_TYPES.has(header?.type)) {
    if (value === '' || value === null || value === undefined) return '';
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : '';
  }
  return value === null || value === undefined ? '' : `${value}`;
};

const toNumericValue = (value) => {
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (Array.isArray(value)) return value.length;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
};

const readRawValue = (header, rowValues = {}) => {
  if (header?.type === 'checkbox') return Boolean(rowValues?.[header.id]);
  if (header?.type === 'multiselect' || header?.type === 'list') {
    return Array.isArray(rowValues?.[header.id]) ? rowValues[header.id] : [];
  }
  return rowValues?.[header.id] ?? '';
};

const normalizeLookupCompareValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => `${item ?? ''}`.trim()).filter(Boolean).join(', ');
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  return `${value ?? ''}`.trim();
};

const getComparableFilterValue = (value) => {
  const normalizedScalar = normalizeLookupCompareValue(value);
  const numeric = Number(normalizedScalar);
  if (normalizedScalar !== '' && Number.isFinite(numeric)) {
    return { kind: 'number', value: numeric };
  }

  const dateValue = parseDateValue(normalizedScalar);
  if (dateValue !== null) {
    return { kind: 'date', value: dateValue };
  }

  const dateTimeValue = parseDateTimeValue(normalizedScalar);
  if (dateTimeValue !== null) {
    return { kind: 'datetime', value: dateTimeValue };
  }

  const timeValue = parseTimeValueToMinutes(normalizedScalar);
  if (timeValue !== null) {
    return { kind: 'time', value: timeValue };
  }

  return { kind: 'text', value: normalizedScalar.toLowerCase() };
};

const compareFilterListValues = (sourceValue, matchValue, operator = 'equals') => {
  const normalizedOperator = FILTER_LIST_OPERATORS.has(`${operator || ''}`.trim()) ? `${operator || ''}`.trim() : 'equals';
  const sourceList = uniqueListValue(sourceValue).map((item) => item.toLowerCase());
  const matchList = uniqueListValue(matchValue).map((item) => item.toLowerCase());

  if (normalizedOperator === 'includes') {
    if (sourceList.length && matchList.length) {
      return matchList.every((item) => sourceList.includes(item));
    }
    return normalizeLookupCompareValue(sourceValue).toLowerCase().includes(normalizeLookupCompareValue(matchValue).toLowerCase());
  }

  if (normalizedOperator === 'in') {
    if (sourceList.length && matchList.length) {
      return sourceList.every((item) => matchList.includes(item));
    }
    return normalizeLookupCompareValue(matchValue).toLowerCase().includes(normalizeLookupCompareValue(sourceValue).toLowerCase());
  }

  if (normalizedOperator === 'equals') {
    return normalizeLookupCompareValue(sourceValue) === normalizeLookupCompareValue(matchValue);
  }

  if (normalizedOperator === 'notEquals') {
    return normalizeLookupCompareValue(sourceValue) !== normalizeLookupCompareValue(matchValue);
  }

  const normalizedSource = getComparableFilterValue(sourceValue);
  const normalizedMatch = getComparableFilterValue(matchValue);
  if (normalizedSource.kind !== normalizedMatch.kind) return false;

  if (normalizedOperator === 'greaterThan') return normalizedSource.value > normalizedMatch.value;
  if (normalizedOperator === 'lessThan') return normalizedSource.value < normalizedMatch.value;
  if (normalizedOperator === 'greaterThanOrEqual') return normalizedSource.value >= normalizedMatch.value;
  if (normalizedOperator === 'lessThanOrEqual') return normalizedSource.value <= normalizedMatch.value;
  return false;
};

const resolveFilterList = (args = [], resolveFilterListValue) => {
  if (typeof resolveFilterListValue !== 'function' || args.length < 2) return [];

  const sheetId = `${args[0] ?? ''}`.trim();
  const returnHeaderId = `${args[1] ?? ''}`.trim();
  if (!sheetId || !returnHeaderId) return [];

  const filterPairs = [];
  for (let index = 2; index < args.length;) {
    const sourceHeaderId = `${args[index] ?? ''}`.trim();
    if (!sourceHeaderId) {
      index += 1;
      continue;
    }
    const maybeOperator = `${args[index + 1] ?? ''}`.trim();
    const hasExplicitOperator = FILTER_LIST_OPERATORS.has(maybeOperator);
    const operator = hasExplicitOperator ? maybeOperator : 'equals';
    const matchValue = index + (hasExplicitOperator ? 2 : 1) < args.length ? args[index + (hasExplicitOperator ? 2 : 1)] : '';
    filterPairs.push({
      sourceHeaderId,
      operator,
      matchValue
    });
    index += hasExplicitOperator ? 3 : 2;
  }

  return uniqueListValue(resolveFilterListValue({
    sheetId,
    returnHeaderId,
    filters: filterPairs
  }));
};

const evaluateArithmeticExpression = (expression, getReferenceValue, resolveLookupValue, resolveFilterListValue) => {
  let expandedExpression = `${expression || ''}`;
  let previousExpression = null;

  while (expandedExpression !== previousExpression) {
    previousExpression = expandedExpression;
    expandedExpression = expandedExpression.replace(/([A-Z_]+)\(([^()]*)\)/gi, (match, fnName, argsSource) => {
      const upperName = fnName.toUpperCase();
      if (!FORMULA_FUNCTIONS.has(upperName)) return match;
      const evaluated = evaluateFormulaExpression(`${upperName}(${argsSource})`, getReferenceValue, resolveLookupValue, resolveFilterListValue);
      return `${toNumericValue(evaluated)}`;
    });
  }

  const withBaseReferences = expandedExpression.replace(/BASE\(([A-Z]+)\)/gi, (_, reference) => `${toNumericValue(getReferenceValue(reference, 'base'))}`);
  const replaced = withBaseReferences.replace(/\b([A-Z]+)\b/g, (_, reference) => `${toNumericValue(getReferenceValue(reference, 'value'))}`);
  if (/[^0-9+\-*/().\s]/.test(replaced)) return '';

  const input = replaced.replace(/\s+/g, '');
  let pointer = 0;

  const parseExpression = () => {
    let value = parseTerm();
    while (pointer < input.length) {
      const operator = input[pointer];
      if (operator !== '+' && operator !== '-') break;
      pointer += 1;
      const nextValue = parseTerm();
      value = operator === '+' ? value + nextValue : value - nextValue;
    }
    return value;
  };

  const parseTerm = () => {
    let value = parseFactor();
    while (pointer < input.length) {
      const operator = input[pointer];
      if (operator !== '*' && operator !== '/') break;
      pointer += 1;
      const nextValue = parseFactor();
      value = operator === '*' ? value * nextValue : value / nextValue;
    }
    return value;
  };

  const parseFactor = () => {
    if (input[pointer] === '+') {
      pointer += 1;
      return parseFactor();
    }

    if (input[pointer] === '-') {
      pointer += 1;
      return -parseFactor();
    }

    if (input[pointer] === '(') {
      pointer += 1;
      const value = parseExpression();
      if (input[pointer] !== ')') throw new Error('Mismatched parentheses');
      pointer += 1;
      return value;
    }

    const start = pointer;
    while (pointer < input.length && /[0-9.]/.test(input[pointer])) {
      pointer += 1;
    }
    const token = input.slice(start, pointer);
    if (!token) throw new Error('Invalid formula token');
    const numeric = Number(token);
    if (!Number.isFinite(numeric)) throw new Error('Invalid number');
    return numeric;
  };

  try {
    const result = parseExpression();
    if (pointer !== input.length || !Number.isFinite(result)) return '';
    return result;
  } catch (error) {
    return '';
  }
};

const evaluateFormulaExpression = (formula, getReferenceValue, resolveLookupValue, resolveFilterListValue) => {
  const normalized = `${formula || ''}`.trim().replace(/^=/, '');
  if (!normalized) return '';

  const functionMatch = normalized.match(/^([A-Z_]+)\((.*)\)$/i);
  if (functionMatch && FORMULA_FUNCTIONS.has(functionMatch[1].toUpperCase())) {
    const functionName = functionMatch[1].toUpperCase();
    const args = splitFormulaArgs(functionMatch[2]).map((arg) => {
      if ((arg.startsWith('"') && arg.endsWith('"')) || (arg.startsWith('\'') && arg.endsWith('\''))) {
        return arg.slice(1, -1);
      }
      const baseMatch = arg.match(/^BASE\(([A-Z]+)\)$/i);
      if (baseMatch) {
        return getReferenceValue(baseMatch[1].toUpperCase(), 'base');
      }
      if (/^[A-Z]+$/i.test(arg)) {
        return getReferenceValue(arg.toUpperCase(), 'value');
      }
      if (/^-?\d+(\.\d+)?$/.test(arg)) {
        return Number(arg);
      }
      return evaluateFormulaExpression(arg, getReferenceValue, resolveLookupValue, resolveFilterListValue);
    });

    if (functionName === 'CONCAT') {
      return args.map((value) => (Array.isArray(value) ? value.join(', ') : `${value ?? ''}`)).join('');
    }

    if (functionName === 'DATEDIFF_DAYS') {
      if (args.length < 2) return '';
      const endTimestamp = parseDateValue(args[0]);
      const startTimestamp = parseDateValue(args[1]);
      if (endTimestamp === null || startTimestamp === null) return '';
      const weekendMode = `${args[2] ?? ''}`.trim().toLowerCase();
      return weekendMode === 'exclude_weekends'
        ? countWeekdaysBetweenDates(startTimestamp, endTimestamp)
        : Math.round((endTimestamp - startTimestamp) / (24 * 60 * 60 * 1000));
    }

    if (functionName === 'DATETIMEDIFF_DH') {
      if (args.length < 2) return '';
      const endTimestamp = parseDateTimeValue(args[0]);
      const startTimestamp = parseDateTimeValue(args[1]);
      if (endTimestamp === null || startTimestamp === null) return '';
      return formatDayHourDuration(endTimestamp - startTimestamp);
    }

    if (functionName === 'TIMEDIFF_HOURS') {
      if (args.length < 2) return '';
      const endMinutes = parseTimeValueToMinutes(args[0]);
      const startMinutes = parseTimeValueToMinutes(args[1]);
      if (endMinutes === null || startMinutes === null) return '';
      return Math.round((((endMinutes - startMinutes) / 60) + Number.EPSILON) * 100) / 100;
    }

    if (functionName === 'DAY') {
      if (args.length < 1) return '';
      const date = parseDateObjectValue(args[0]);
      if (!date) return '';
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    }

    if (functionName === 'WEEK') {
      if (args.length < 1) return '';
      const date = parseDateObjectValue(args[0]);
      if (!date) return '';
      return getIsoWeekNumber(date);
    }

    if (functionName === 'MONTH') {
      if (args.length < 1) return '';
      const date = parseDateObjectValue(args[0]);
      if (!date) return '';
      return date.toLocaleDateString('en-US', { month: 'long' });
    }

    if (functionName === 'LOOKUP') {
      if (args.length < 4 || typeof resolveLookupValue !== 'function') return '';
      return resolveLookupValue({
        lookupSheetId: `${args[0] ?? ''}`.trim(),
        lookupMatchSourceHeaderId: `${args[1] ?? ''}`.trim(),
        lookupMatchCurrentHeaderId: `${args[2] ?? ''}`.trim(),
        lookupReturnHeaderId: `${args[3] ?? ''}`.trim()
      });
    }

    if (functionName === 'FILTERLIST') {
      return resolveFilterList(args, resolveFilterListValue);
    }

    if (functionName === 'SUBTRACTLIST') {
      const sourceList = uniqueListValue(args[0]);
      const subtractList = new Set(uniqueListValue(args[1]).map((item) => `${item}`));
      return sourceList.filter((item) => !subtractList.has(`${item}`));
    }

    const numericArgs = args.map((value) => toNumericValue(value));
    if (!numericArgs.length) return '';

    if (functionName === 'SUM') return numericArgs.reduce((total, value) => total + value, 0);
    if (functionName === 'AVG') return numericArgs.reduce((total, value) => total + value, 0) / numericArgs.length;
    if (functionName === 'MIN') return Math.min(...numericArgs);
    if (functionName === 'MAX') return Math.max(...numericArgs);
  }

  if (/^[A-Z]+$/i.test(normalized)) {
    return getReferenceValue(normalized.toUpperCase(), 'value');
  }

  const baseMatch = normalized.match(/^BASE\(([A-Z]+)\)$/i);
  if (baseMatch) {
    return getReferenceValue(baseMatch[1].toUpperCase(), 'base');
  }

  return evaluateArithmeticExpression(normalized, getReferenceValue, resolveLookupValue, resolveFilterListValue);
};

export const resolveRowFormulas = (headers = [], rowValues = {}, referenceSheetsById = {}) => {
  const lettersByHeaderId = headers.reduce((acc, header, index) => {
    acc[header.id] = getColumnLetter(index);
    return acc;
  }, {});
  const headersByLetter = headers.reduce((acc, header) => {
    acc[lettersByHeaderId[header.id]] = header;
    return acc;
  }, {});
  const cache = {};

  const resolveHeaderValue = (header, stack = new Set()) => {
    if (!header?.id) return '';
    if (Object.prototype.hasOwnProperty.call(cache, header.id)) {
      return cache[header.id];
    }

    if (stack.has(header.id)) {
      return '';
    }

    const conditionalFormulaRules = sanitizeHeaderConditionalFormulaRules(header?.conditionalFormulaRules, headers, header?.id);

    if (!hasHeaderFormula(header) && !conditionalFormulaRules.length) {
      const rawValue = readRawValue(header, rowValues);
      const resolved = coerceResolvedValue(header, rawValue);
      cache[header.id] = resolved;
      return resolved;
    }

    stack.add(header.id);
    const activeConditionalRule = conditionalFormulaRules.find((rule) => {
      const referencedHeader = headers.find((candidate) => candidate.id === rule.headerId);
      const ruleValue = referencedHeader ? resolveHeaderValue(referencedHeader, stack) : rowValues?.[rule.headerId];
      return evalConditionalRule(rule, ruleValue, cache, headers, header.id);
    });
    const selectedFormula = `${activeConditionalRule?.formula || header.formula || ''}`.trim();
    const formulaBody = selectedFormula.replace(/^=/, '').trim();
    const isPlainHeaderTemplate = /\[[^\][]+\]/.test(formulaBody) && !/[+\-*/()]|^[A-Z_]+\s*\(/i.test(formulaBody);
    if (isPlainHeaderTemplate) {
      const resolvedTemplate = coerceResolvedValue(
        header,
        resolveHeaderReferences(formulaBody, headers, cache, header.id)
      );
      stack.delete(header.id);
      cache[header.id] = resolvedTemplate;
      return resolvedTemplate;
    }
    const formulaWithHeaderReferences = resolveHeaderFormulaReferences(
      selectedFormula,
      headers,
      header.id,
      (referencedHeader) => lettersByHeaderId[referencedHeader.id] || ''
    );
    const parsedFormulaConfig = parseFormulaToConfig(formulaWithHeaderReferences, headers, header.id);
    const normalizedFormula = parsedFormulaConfig
      ? (buildFormulaFromConfig(parsedFormulaConfig, headers) || selectedFormula)
      : formulaWithHeaderReferences;
    const resolved = coerceResolvedValue(
      header,
      evaluateFormulaExpression(
        normalizedFormula,
        (reference, referenceKind = 'value') => {
          const referencedHeader = headersByLetter[reference];
          if (!referencedHeader) return '';
          if (referenceKind === 'base') {
            const rawBaseValue = referencedHeader.baseValue;
            if (rawBaseValue === '' || rawBaseValue === null || rawBaseValue === undefined) return '';
            const numericBaseValue = Number(rawBaseValue);
            return Number.isFinite(numericBaseValue) ? numericBaseValue : '';
          }
          return resolveHeaderValue(referencedHeader, stack);
        },
        (lookupConfig) => {
          const sourceSheetId = `${lookupConfig?.lookupSheetId || ''}`.trim();
          const sourceMatchHeaderId = `${lookupConfig?.lookupMatchSourceHeaderId || ''}`.trim();
          const currentMatchHeaderId = `${lookupConfig?.lookupMatchCurrentHeaderId || ''}`.trim();
          const returnHeaderId = `${lookupConfig?.lookupReturnHeaderId || ''}`.trim();

          if (!sourceSheetId || !sourceMatchHeaderId || !currentMatchHeaderId || !returnHeaderId) {
            return '';
          }

          const currentHeader = headers.find((candidate) => candidate.id === currentMatchHeaderId);
          const currentValue = currentHeader ? resolveHeaderValue(currentHeader, stack) : rowValues?.[currentMatchHeaderId];
          const normalizedCurrentValue = normalizeLookupCompareValue(currentValue);
          if (!normalizedCurrentValue) return '';

          const sourceData = referenceSheetsById?.[sourceSheetId] || {};
          const sourceRows = Array.isArray(sourceData?.rows) ? sourceData.rows : [];
          const sourceHeaders = Array.isArray(sourceData?.headers) ? sourceData.headers : [];
          const sourceMatchHeader = sourceHeaders.find((candidate) => candidate?.id === sourceMatchHeaderId);
          const returnHeader = sourceHeaders.find((candidate) => candidate?.id === returnHeaderId);
          if (!sourceMatchHeader || !returnHeader) return '';

          const matchingRow = sourceRows.find((row) => {
            const sourceValue = row?.resolvedValues?.[sourceMatchHeaderId] ?? row?.values?.[sourceMatchHeaderId];
            return normalizeLookupCompareValue(sourceValue) === normalizedCurrentValue;
          });

          if (!matchingRow) return '';

          return matchingRow?.resolvedValues?.[returnHeaderId] ?? matchingRow?.values?.[returnHeaderId] ?? '';
        },
        ({ sheetId, returnHeaderId, filters = [] }) => {
          const sourceSheetId = `${sheetId || ''}`.trim();
          const normalizedReturnHeaderId = `${returnHeaderId || ''}`.trim();
          if (!sourceSheetId || !normalizedReturnHeaderId) return [];

          const sourceData = referenceSheetsById?.[sourceSheetId] || {};
          const sourceRows = Array.isArray(sourceData?.rows) ? sourceData.rows : [];
          const sourceHeaders = Array.isArray(sourceData?.headers) ? sourceData.headers : [];
          const returnHeader = sourceHeaders.find((candidate) => candidate?.id === normalizedReturnHeaderId);
          if (!returnHeader) return [];

          const matchingRows = sourceRows.filter((row) => (
            (Array.isArray(filters) ? filters : []).every((filter) => {
              const sourceHeaderId = `${filter?.sourceHeaderId || ''}`.trim();
              if (!sourceHeaderId) return true;
              const sourceValue = row?.resolvedValues?.[sourceHeaderId] ?? row?.values?.[sourceHeaderId];
              return compareFilterListValues(sourceValue, filter?.matchValue, filter?.operator);
            })
          ));

          return uniqueListValue(
            matchingRows.flatMap((row) => {
              const rawValue = row?.resolvedValues?.[normalizedReturnHeaderId] ?? row?.values?.[normalizedReturnHeaderId];
              return Array.isArray(rawValue) ? rawValue : [rawValue];
            })
          );
        }
      )
    );
    stack.delete(header.id);
    cache[header.id] = resolved;
    return resolved;
  };

  return headers.reduce((acc, header) => {
    acc[header.id] = resolveHeaderValue(header);
    return acc;
  }, {});
};
